'use client'

import { useState, useRef, useCallback, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { parse as csvParse } from 'papaparse'
import { popModal } from '@/components/modals'
import {
  Upload,
  FileSpreadsheet,
  AlertTriangle,
  Loader2,
  ChevronRight,
} from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'

interface ImportCsvModalProps {
  projectId: string
  onClose?: () => void
  onImportComplete?: () => void
}

interface ColumnMapping {
  csvColumn: string
  customerField: string | null
}

interface ParsedData {
  headers: string[]
  rows: string[][]
  hasEmailColumn: boolean
  emailColumnIndex: number
}

const CUSTOMER_FIELDS = [
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'address', label: 'Address' },
  { value: null, label: 'Skip' },
]

export default function ImportCsvModal({
  projectId,
  onClose,
  onImportComplete,
}: ImportCsvModalProps) {
  const t = useTranslations('customers.import_csv')
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [isGuessing, setIsGuessing] = useState(false)
  const [parsedData, setParsedData] = useState<ParsedData | null>(null)
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [previewRows, setPreviewRows] = useState(5)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Validate if CSV has required email column or can be detected
  const validateEmailColumn = useCallback(
    (headers: string[], rows: string[][]): number => {
      // Check if headers contain email
      const emailHeaderIndex = headers.findIndex((header) =>
        header.toLowerCase().includes('email'),
      )
      if (emailHeaderIndex !== -1) return emailHeaderIndex

      // Try to detect email column by content
      if (rows.length > 0) {
        for (let colIndex = 0; colIndex < headers.length; colIndex++) {
          // Check first few rows for email patterns
          const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          const hasEmails = rows
            .slice(0, Math.min(5, rows.length))
            .some((row) => emailPattern.test(row[colIndex] || ''))
          if (hasEmails) return colIndex
        }
      }

      return -1
    },
    [],
  )

  // Guess column mappings using AI
  const guessColumnMappings = useCallback(
    async (headers: string[], sampleRows: string[][]) => {
      setIsGuessing(true)
      try {
        const response = await fetch('/api/email/ai-guess-column', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            headers,
            sample_rows: sampleRows.slice(0, 10),
          }),
        })

        if (response.ok) {
          const data = await response.json()
          if (data.success && data.mappings) {
            return data.mappings
          }
        }
      } catch (error) {
        console.error('Error guessing columns:', error)
      } finally {
        setIsGuessing(false)
      }

      // Fallback to basic mapping
      return headers.map((header) => ({
        csvColumn: header,
        customerField: null,
      }))
    },
    [],
  )

  // Parse CSV file
  const parseCSVFile = useCallback(
    async (file: File): Promise<void> => {
      return new Promise((resolve, reject) => {
        csvParse(file, {
          header: false,
          skipEmptyLines: true,
          complete: async (results) => {
            if (results.errors.length > 0) {
              reject(new Error(t('csv_parsing_error')))
              return
            }

            const data = results.data as string[][]
            if (data.length === 0) {
              reject(new Error(t('empty_file')))
              return
            }

            // First row as headers
            const headers = data[0]
            const rows = data.slice(1)

            // Check for email column
            const emailColumnIndex = validateEmailColumn(headers, rows)
            const hasEmailColumn = emailColumnIndex !== -1

            setParsedData({
              headers,
              rows,
              hasEmailColumn,
              emailColumnIndex,
            })

            // Set up column mappings
            let mappings: ColumnMapping[]

            if (hasEmailColumn) {
              // Auto-map common fields
              mappings = headers.map((header) => {
                const lowerHeader = header.toLowerCase()
                let customerField = null

                if (lowerHeader.includes('email')) customerField = 'email'
                else if (
                  lowerHeader.includes('first') &&
                  lowerHeader.includes('name')
                )
                  customerField = 'first_name'
                else if (
                  lowerHeader.includes('last') &&
                  lowerHeader.includes('name')
                )
                  customerField = 'last_name'
                else if (lowerHeader.includes('phone')) customerField = 'phone'
                else if (lowerHeader.includes('address'))
                  customerField = 'address'

                return { csvColumn: header, customerField }
              })
            } else {
              // Use AI to guess columns
              mappings = await guessColumnMappings(headers, rows)

              // Ensure email column is mapped if detected
              if (emailColumnIndex !== -1) {
                mappings[emailColumnIndex].customerField = 'email'
              }
            }

            setColumnMappings(mappings)
            resolve()
          },
          error: () => {
            reject(new Error(t('csv_parsing_error')))
          },
        })
      })
    },
    [validateEmailColumn, guessColumnMappings, t],
  )

  // Handle file upload
  const handleFileUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0) return

      const file = files[0]
      const fileExtension = file.name.split('.').pop()?.toLowerCase()

      if (fileExtension !== 'csv') {
        setErrorMessage(t('invalid_file_type'))
        return
      }

      // Check file size limit (5MB)
      const maxSizeInBytes = 5 * 1024 * 1024
      if (file.size > maxSizeInBytes) {
        setErrorMessage(t('file_too_large'))
        return
      }

      setIsUploading(true)
      setErrorMessage(null)

      try {
        await parseCSVFile(file)
      } catch (error) {
        setErrorMessage(
          error instanceof Error ? error.message : t('upload_failed'),
        )
      } finally {
        setIsUploading(false)
      }
    },
    [parseCSVFile, t],
  )

  // Update column mapping
  const updateColumnMapping = useCallback(
    (index: number, customerField: string | null) => {
      setColumnMappings((prev) =>
        prev.map((mapping, i) =>
          i === index ? { ...mapping, customerField } : mapping,
        ),
      )
    },
    [],
  )

  // Import customers to database
  const handleImport = useCallback(async () => {
    if (!parsedData) return

    // Check if email column is mapped
    const emailMapping = columnMappings.find((m) => m.customerField === 'email')
    if (!emailMapping) {
      setErrorMessage(t('email_column_required'))
      return
    }

    setIsImporting(true)
    setErrorMessage(null)

    try {
      // Prepare customer data for backend
      const customers = parsedData.rows
        .map((row) => {
          const customer: any = {}

          // Map CSV columns to customer fields
          columnMappings.forEach((mapping, index) => {
            if (mapping.customerField && row[index]) {
              customer[mapping.customerField] = row[index].trim()
            }
          })

          // Skip rows without email
          if (!customer.email) return null

          // Ensure required fields have at least empty strings
          return {
            first_name: customer.first_name || '',
            last_name: customer.last_name || '',
            email: customer.email,
            phone: customer.phone || null,
            address: customer.address || null,
          }
        })
        .filter(Boolean)

      if (customers.length === 0) {
        setErrorMessage(t('no_valid_customers'))
        return
      }

      // Send customer data to secure backend endpoint
      const response = await fetch(
        `/api/me/project/${projectId}/customers/import`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customers }),
        },
      )

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.message || t('import_failed'))
      }

      const result = await response.json()

      if (result.success) {
        // Show success message with import results
        if (result.data.failed > 0) {
          toast.warning(
            t('import_partial_success', {
              imported: result.data.imported,
              failed: result.data.failed,
            }),
          )
        } else {
          toast.success(t('import_success', { count: result.data.imported }))
        }
      } else {
        throw new Error(result.message || t('import_failed'))
      }
      onImportComplete?.()
      popModal('ImportCsvModal')
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : t('import_failed'),
      )
    } finally {
      setIsImporting(false)
    }
  }, [parsedData, columnMappings, projectId, t, onImportComplete])

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      handleFileUpload(e.dataTransfer.files)
    },
    [handleFileUpload],
  )

  // Get preview data for table
  const previewData = useMemo(() => {
    if (!parsedData) return []
    return parsedData.rows.slice(0, previewRows)
  }, [parsedData, previewRows])

  // Get mapped headers for preview
  const mappedHeaders = useMemo(() => {
    return columnMappings.map((mapping) => {
      const field = CUSTOMER_FIELDS.find(
        (f) => f.value === mapping.customerField,
      )
      return field?.label || t('skip')
    })
  }, [columnMappings, t])

  // Handle close
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose()
    } else {
      popModal('ImportCsvModal')
    }
  }, [onClose])

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-[#222222]/95 via-[#2B2B2B] to-[#010101]/95">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5" />
            {t('title')}
          </DialogTitle>
          <DialogDescription className="text-white/60">
            {parsedData ? t('map_columns') : t('description')}
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Error Alert */}
          {errorMessage && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          )}

          {!parsedData ? (
            /* Upload Area */
            <div
              className={`border-2 border-dashed rounded-md p-8 text-center transition-all duration-200 ${
                isDragging
                  ? 'border-white/40 bg-white/5'
                  : 'border-white/20 hover:border-white/30 hover:bg-white/5'
              } ${isUploading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center">
                  {isUploading ? (
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  ) : (
                    <Upload className="w-8 h-8 text-white opacity-80" />
                  )}
                </div>

                <div>
                  <h3 className="text-lg font-medium text-white mb-2">
                    {isUploading ? t('processing') : t('drop_file')}
                  </h3>
                  <p className="text-white opacity-60 text-sm">
                    {t('supported_format')}
                  </p>
                </div>

                {!isUploading && (
                  <Button variant="secondary" size="sm">
                    {t('choose_file')}
                  </Button>
                )}
              </div>

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".csv"
                onChange={(e) => handleFileUpload(e.target.files)}
                disabled={isUploading}
              />
            </div>
          ) : (
            /* Data Preview and Mapping */
            <div className="space-y-6">
              {/* Column Mappings */}
              {isGuessing && (
                <Alert className="bg-blue-500/10 border-blue-500/30">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <AlertDescription className="text-blue-300">
                    {t('guessing_columns')}
                  </AlertDescription>
                </Alert>
              )}

              {!parsedData.hasEmailColumn && !isGuessing && (
                <Alert className="bg-amber-500/10 border-amber-500/30">
                  <AlertTriangle className="h-4 w-4 text-amber-400" />
                  <AlertDescription>
                    <p className="text-amber-300 font-medium">
                      {t('no_email_header')}
                    </p>
                    <p className="text-amber-200 text-xs mt-1 opacity-80">
                      {t('ai_guessed_columns')}
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              <div>
                <h3 className="text-white font-medium mb-3">
                  {t('column_mapping')}
                </h3>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                  {columnMappings.map((mapping, index) => (
                    <div
                      key={`${mapping.csvColumn}-${index}`}
                      className="flex items-center gap-2 p-2 bg-white/5 rounded-md"
                    >
                      <span className="text-white opacity-80 text-sm flex-1 truncate">
                        {mapping.csvColumn}
                      </span>
                      <ChevronRight className="w-4 h-4 text-white opacity-40" />
                      <Select
                        value={mapping.customerField || 'skip'}
                        onValueChange={(value) =>
                          updateColumnMapping(
                            index,
                            value === 'skip' ? null : value,
                          )
                        }
                      >
                        <SelectTrigger className="w-[140px] bg-[#1a1a1a] border border-white/20 text-white text-sm h-8">
                          <SelectValue placeholder={t('skip')} />
                        </SelectTrigger>
                        <SelectContent>
                          {CUSTOMER_FIELDS.map((field) => (
                            <SelectItem
                              key={field.value || 'skip'}
                              value={field.value || 'skip'}
                            >
                              {field.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>

              {/* Data Preview */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-medium">{t('preview')}</h3>
                  <span className="text-white opacity-60 text-sm">
                    {t('showing_rows', {
                      count: previewRows,
                      total: parsedData.rows.length,
                    })}
                  </span>
                </div>

                <div className="border border-white/10 rounded-md overflow-hidden">
                  <div className="max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader className="bg-[#222222] sticky top-0">
                        <TableRow>
                          {parsedData.headers.map((header, index) => (
                            <TableHead
                              key={`${header}-${index}`}
                              className="text-white opacity-80 border-r border-white/10 last:border-r-0"
                            >
                              <div>
                                <div className="font-medium">{header}</div>
                                <div className="text-xs opacity-60 mt-1">
                                  → {mappedHeaders[index]}
                                </div>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {previewData.map((row, rowIndex) => (
                          <TableRow key={`row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                              <TableCell
                                key={`cell-${rowIndex}-${cellIndex}`}
                                className="text-white opacity-80 border-r border-white/10 last:border-r-0"
                              >
                                {cell || '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {parsedData.rows.length > previewRows && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setPreviewRows(previewRows + 5)}
                    className="mt-2 text-white opacity-60 hover:opacity-80"
                    size="sm"
                  >
                    {t('show_more')}
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          {parsedData && (
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setParsedData(null)
                  setColumnMappings([])
                }}
                className="text-white/60"
              >
                {t('upload_different')}
              </Button>

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={handleClose}
                  disabled={isImporting}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant="default"
                  onClick={handleImport}
                  disabled={isImporting}
                  className="bg-white text-black hover:bg-white/90"
                >
                  {isImporting && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {isImporting
                    ? t('importing')
                    : t('import_count', { count: parsedData.rows.length })}
                </Button>
              </div>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
